import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useAuthStore } from '../../../store/authStore';
import { UserService } from '../../../services/userService';

type ProfileSettingsForm = {
  is_public: boolean;
  bio: string;
  location: string;
  website: string;
  notifications_enabled: boolean;
};

const ProfileTab: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ProfileSettingsForm>({
    is_public: true,
    bio: '',
    location: '',
    website: '',
    notifications_enabled: true,
  });

  const isAuthed = Boolean(user?.id);

  const canSave = useMemo(() => {
    if (!isAuthed) return false;
    if (saving) return false;
    return true;
  }, [isAuthed, saving]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!user?.id) return;
      try {
        setLoading(true);
        const profile = await UserService.getUserProfile(user.id);
        if (!mounted) return;

        setForm({
          is_public: Boolean(profile?.is_public ?? true),
          bio: String(profile?.bio ?? ''),
          location: String(profile?.location ?? ''),
          website: String(profile?.website ?? ''),
          notifications_enabled: Boolean(profile?.notifications_enabled ?? true),
        });
      } catch (e) {
        console.error('Load profile settings failed', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const handleSave = async () => {
    if (!user?.id) return;
    try {
      setSaving(true);

      const updates = {
        is_public: form.is_public,
        bio: form.bio,
        location: form.location,
        website: form.website,
        notifications_enabled: form.notifications_enabled,
        updated_at: new Date().toISOString(),
      };

      const res = await UserService.updateUserProfileDirect(user.id, updates);
      if (!res.success) {
        toast.error(res.error || t('common.save_failed') || 'Save failed');
        return;
      }

      toast.success(t('common.saved') || 'Saved');
    } catch (e: any) {
      console.error('Save profile settings failed', e);
      toast.error(t('common.save_failed') || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.75 }}>
        {t('settings.profile.title') || 'Profil'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('settings.profile.subtitle') || 'Passe an, wie dein Profil angezeigt wird und welche Infos sichtbar sind.'}
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.06)',
          background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)',
        }}
      >
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              {t('settings.profile.section.visibility') || 'Sichtbarkeit'}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_public}
                  onChange={(e) => setForm(prev => ({ ...prev, is_public: e.target.checked }))}
                />
              }
              label={
                form.is_public
                  ? (t('settings.profile.visibility.public') || 'Öffentliches Profil')
                  : (t('settings.profile.visibility.private') || 'Privates Profil')
              }
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {form.is_public
                ? (t('settings.profile.visibility.publicHelp') || 'Jeder kann dein Profil sehen.')
                : (t('settings.profile.visibility.privateHelp') || 'Nur du (und ggf. freigegebene Nutzer) können dein Profil sehen.')}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              {t('settings.profile.section.info') || 'Profilinfos'}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label={t('settings.profile.field.location') || 'Ort'}
                value={form.location}
                onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                fullWidth
              />
              <TextField
                label={t('settings.profile.field.website') || 'Website'}
                value={form.website}
                onChange={(e) => setForm(prev => ({ ...prev, website: e.target.value }))}
                fullWidth
              />
              <TextField
                label={t('settings.profile.field.bio') || 'Bio'}
                value={form.bio}
                onChange={(e) => setForm(prev => ({ ...prev, bio: e.target.value }))}
                fullWidth
                multiline
                minRows={3}
                sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              {t('settings.profile.section.preferences') || 'Präferenzen'}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={form.notifications_enabled}
                  onChange={(e) => setForm(prev => ({ ...prev, notifications_enabled: e.target.checked }))}
                />
              }
              label={t('settings.profile.notifications') || 'Benachrichtigungen aktivieren'}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!canSave}
                startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  fontWeight: 900,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 10px 24px rgba(102, 126, 234, 0.24)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7286ff 0%, #8357b2 100%)',
                    boxShadow: '0 14px 30px rgba(102, 126, 234, 0.32)',
                  },
                }}
              >
                {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ProfileTab;
